import React, { useEffect, useMemo, useState } from 'react';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import { KendoReactTextArea } from '@Shared.Angular/kendo.react/KendoReactTextArea';
import { FileUpload } from '@Shared.Angular/flowingly.components/FileUpload/FileUpload';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';

export interface CreationHubProcedureDetails {
  name: string;
  description: string;
  categoryId?: Guid;
  videoBlob?: Blob | null;
  ownerUserId?: Guid | null;
  objective?: string;
  background?: string;
}

type CreationHubProcedureDetailsPanelProps = {
  details: CreationHubProcedureDetails;
  onDetailsChange: (model: CreationHubProcedureDetails) => void;
  isDisabled?: boolean;
  showNameExistsError?: boolean;
  onValidationChange?: (validationError: string) => void;
  showFileUpload?: boolean;
};

export const CreationHubProcedureDetailsPanel = ({
  details,
  onDetailsChange,
  isDisabled = false,
  showNameExistsError = false,
  onValidationChange,
  showFileUpload = false
}: CreationHubProcedureDetailsPanelProps) => {
  const [categories, setCategories] = useState<ICategoryDetail[]>([]);

  useEffect(() => {
    getService('categoryApiService')
      .getCachedCategories()
      .then((categoryDetails) => {
        setCategories(categoryDetails);
      });
  }, []);

  const APP_CONFIG = getService('APP_CONFIG');

  const nameXssValidation = useMemo(
    () => XssValidation(details.name),
    [details.name]
  );
  const descriptionXssValidation = useMemo(
    () => XssValidation(details.description),
    [details.description]
  );
  const objectiveXssValidation = useMemo(
    () => XssValidation(details.objective),
    [details.objective]
  );
  const backgroundXssValidation = useMemo(
    () => XssValidation(details.background),
    [details.background]
  );

  const xssErrorExists = nameXssValidation || descriptionXssValidation;

  const validationError =
    !details.name || details.name.trim() === ''
      ? 'Name is required'
      : APP_CONFIG.enableGovernance && !details.categoryId
      ? 'Category is required'
      : xssErrorExists
      ? 'Please fix validation errors'
      : '';

  useEffect(() => {
    onValidationChange?.(validationError);
  }, [validationError]);

  return (
    <div className="creation-hub-procedure-details-panel flowingly-form">
      {showNameExistsError && (
        <div className="validation-errors">
          <p className="center-align">
            There is already a SOP with this name. Please enter a unique name.
          </p>
        </div>
      )}
      <div className="flowingly-field">
        <label htmlFor="procedure-details-name">Name *</label>
        <input
          value={details.name}
          onChange={(e) =>
            onDetailsChange({ ...details, name: e.target.value })
          }
          name="procedure-details-name"
          id="procedure-details-name"
          type="text"
          required
          autoComplete="off"
          disabled={isDisabled}
        />
        {nameXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="procedure-details-description">Description</label>
        <KendoReactTextArea
          placeholder="Short summary"
          value={details.description}
          onChange={(e) =>
            onDetailsChange({ ...details, description: e.target.value })
          }
          id="procedure-details-description"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {descriptionXssValidation}
      </div>
      {APP_CONFIG.enableGovernance && (
        <div className="flowingly-field">
          <label htmlFor="procedure-details-category-dropdown">
            Category *
          </label>
          <KendoReactDropDownTree
            id="procedure-details-category-dropdown"
            categories={categories}
            categoryClicked={(
              categoryId: Guid,
              index,
              name,
              category: ICategoryDetail
            ) => {
              onDetailsChange({
                ...details,
                categoryId: categoryId
              });
            }}
            placeholder="Select a category"
            selectedId={details.categoryId}
            dataItemKey="id"
            textField="name"
            subItemsField="subCategories"
            defaultItemId={null}
            showFilter={true}
            filterThreshold={8}
            disabled={isDisabled}
          />
        </div>
      )}
      <div className="flowingly-field">
        <label htmlFor="procedure-owner">Owner</label>
        <SelectUser
          inputId="procedure-owner"
          selectedUserId={details.ownerUserId}
          onUserSelect={(userId) =>
            onDetailsChange({ ...details, ownerUserId: userId })
          }
          disabled={isDisabled}
          popupClassName="procedure-owner-dropdown-tree-popup modal-overlay"
        />
      </div>
      <div className="flowingly-field">
        <label htmlFor="procedure-details-objective">Objective</label>
        <input
          value={details.objective}
          onChange={(e) =>
            onDetailsChange({ ...details, objective: e.target.value })
          }
          name="procedure-details-objective"
          id="procedure-details-objective"
          type="text"
          autoComplete="off"
          disabled={isDisabled}
        />
        {objectiveXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="procedure-details-background">Background</label>
        <input
          value={details.background}
          onChange={(e) =>
            onDetailsChange({ ...details, background: e.target.value })
          }
          name="procedure-details-background"
          id="procedure-details-background"
          type="text"
          autoComplete="off"
          disabled={isDisabled}
        />
        {backgroundXssValidation}
      </div>
      {showFileUpload && (
        <div className="flowingly-field">
          <label>Upload File</label>
          <FileUpload
            acceptTypes={['.mp4', '.mov', '.webm']}
            onFileSelected={(file) =>
              onDetailsChange({ ...details, videoBlob: file })
            }
            isDisabled={isDisabled}
          />
        </div>
      )}
    </div>
  );
};

const XssValidation = (value: string) => {
  if (getService('validationService').isXssVulnerableString(value)) {
    return <div className="error-text">This input does not support HTML</div>;
  }
  return undefined;
};
